/**
 * Copyright (c) Microsoft. All rights reserved.
 */

// tslint:disable:no-any
// tslint:disable:no-unsafe-any
// tslint:disable:max-classes-per-file
// tslint:disable:variable-name

import { Timespan, toJson } from './Common';
import { CLIENT_VERSION } from './Constants';
import { HttpHeaders, ITelemetrySender, LogProvider, TrouterTelemetrySettings } from './Interfaces';
import { IClientInfo, ITrouterRequest, ServerState } from './InternalInterfaces';
import { Logger } from './Logger';

/**
 * Data for trouter response
 */
export class ResponseData {
    public status: number = 200;
    public headers?: HttpHeaders = {};
    public body: string = '';
    public sentTS: number | undefined;

    public constructor(public id: number) { }
}

// Possible telemetry events
export enum ClientEventName {
    Connected = 'trouter_js_client_connected',
    Disconnected = 'trouter_js_client_disconnected',
    Error = 'trouter_js_client_error',
    Progress = 'trouter_js_client_progress',
    Response = 'trouter_js_client_response',
    Request = 'trouter_js_client_request',
    CheckConnection = 'trouter_js_client_check_connection',
    Registration = 'trouter_js_client_registration',
    Unregistration = 'trouter_js_client_unregistration'
}
export type EnabledEvents = {[key in ClientEventName]: boolean};

// tslint:disable-next-line:no-empty-interface
export interface IAllTelemetryEventsSettings extends EnabledEvents {
}

/**
 * Stores one step of connection process
 */
export class TrackerStep {
    public constructor(
        public stepName: string,
        public operation: string,
        public delta: number,
        public ts: number,
        public error?: string
    ) { }
}

/**
 * Base class for something that can be used as a property bag
 */
export class Properties {
    [index: string]: number | string | boolean | { [key: number]: boolean } | undefined;
}

/**
 * Information about connection
 */
class ConnectedInfo {
    public numberOfPingReplies: number = 0;
    public connectedTimestamp: number = 0;
    public isNewUrl: boolean = false;
    public transportType: string = '';
    public connectionNumber: number = 0;
}

/**
 * Default values for IEventLogSettings
 */
class EventLogSettings implements TrouterTelemetrySettings, IAllTelemetryEventsSettings {
    public enabled = false;
    public numberOfStepsToMaintain = 40;
    public logHealthCheckError = false;
    public sendProgressTimeoutSecs = 55;
    public logSendPingError = false;
    public maxBackoffInMs = 120000;

    public trouter_js_client_connected = false;
    public trouter_js_client_disconnected = false;
    public trouter_js_client_error = false;
    public trouter_js_client_progress = false;
    public trouter_js_client_response = false;
    public trouter_js_client_request = false;
    public trouter_js_client_registration = false;
    public trouter_js_client_unregistration = false;

    /** This event is ENABLED by default */
    public trouter_js_client_check_connection = true;
}

/**
 * Telemetry sender
 */
export class ConnectionTracker {
    private logger: Logger;
    private steps: TrackerStep[];
    private connectionAttempt: number;
    private totalStepCount: number;
    private beginTimestamp: Timespan;
    private eventLogSettings: TrouterTelemetrySettings;
    private connectedInfo: ConnectedInfo;
    private progressTimeout?: number;
    private eventLogger?: ITelemetrySender;

    public constructor(
        logfunc: LogProvider,
        private clientId: number,
        private clientInfo: IClientInfo,
        private getServerState: () => ServerState,
        private endpointId?: string,
        public clientCorrelationID?: string,
        private environment?: string
    ) {
        this.logger = new Logger('ConnectionTracker', logfunc);
        this.clientCorrelationID = clientCorrelationID !== undefined ? clientCorrelationID : '';
        this.steps = [];
        this.connectionAttempt = 0;
        this.totalStepCount = 0;
        this.beginTimestamp = new Timespan();
        this.eventLogSettings = new EventLogSettings();
        this.connectedInfo = new ConnectedInfo();
    }

    public enable(telemetrySender: ITelemetrySender) {
        this.eventLogSettings.enabled = true;
        this.eventLogger = telemetrySender;
    }

    public disable() {
        this.eventLogSettings.enabled = false;
    }

    public sendProgress(context: Properties): void {
        // connect truncates steps array. this check ensures that a progress event is only sent out if the client is not connected
        if (this.steps.length > 0) {
            this.sendTelemetry(ClientEventName.Progress, context, this.steps);
        }
    }

    public cancelProgressTimer(): void {
        if (this.progressTimeout !== undefined) {
            clearTimeout(this.progressTimeout);
            this.progressTimeout = undefined;
        }
    }

    public resetProgressSendTimer() {
        this.cancelProgressTimer();

        // send connection steps after a timeout
        // this timeout may fire if auth callback never comes or the client is stuck in a loop to get connected
        if (this.eventLogSettings.sendProgressTimeoutSecs !== undefined && this.eventLogSettings.sendProgressTimeoutSecs > 0) {
            this.progressTimeout = setTimeout(() => {
                this.sendProgress({
                    reason: 'timeout',
                    timeoutSecs: this.eventLogSettings.sendProgressTimeoutSecs
                });
            }, 1000 * this.eventLogSettings.sendProgressTimeoutSecs);
        }
    }

    public setConnectedInfo(isNewUrl: boolean, transportType: string): void {
        this.connectedInfo.numberOfPingReplies = 0;
        this.connectedInfo.connectedTimestamp = Date.now();
        this.connectedInfo.isNewUrl = isNewUrl;
        this.connectedInfo.transportType = transportType;
        ++this.connectedInfo.connectionNumber;
    }

    public clearConnectedInfo(): void {
        this.connectedInfo.numberOfPingReplies = 0;
        this.connectedInfo.connectedTimestamp = 0;
        this.connectedInfo.isNewUrl = true;
        this.connectedInfo.transportType = '';
    }

    // Copies from
    // source {
    //    propName: value
    // }
    // to
    // dest {
    //    propName: { value: actualValue }
    // }
    public copyProperties(dest: any, source: Properties): void {
        // copy properties from source and add in a format expected by logger
        for (const prop of Object.keys(source)) {
            if (source.hasOwnProperty(prop) && source[prop] !== undefined) {
                // replace hyphen by _
                dest[prop.replace(/-/g, '_')] = { value: source[prop] };
            }
        }
    }

    public increasePingResponseCount() {
        ++this.connectedInfo.numberOfPingReplies;
    }

    /**
     * @param eventName Telemetry event name
     * @param eventProperties Event property bag
     * @param progress
     */
    public sendTelemetry(
        eventName: ClientEventName,
        eventProperties: Properties,
        progress: any[]
    ) {
        try {
            if (this.eventLogSettings.enabled === true
                && this.eventLogSettings[eventName] === true
                && this.eventLogger !== undefined
            ) {
                const serverState = this.getServerState();
                const clientEvent = {
                    name: eventName,
                    properties: {
                        connectionAttempt: { value: this.connectionAttempt },
                        epid: { value: this.endpointId },
                        clientCorrelationID: { value: this.clientCorrelationID },
                        steps: { value: toJson(progress) },
                        clientID: { value: this.clientId },
                        eventVersion: { value: 3 }, // version of telemetry events,
                        environment: { value: this.environment },
                        cv: { value: CLIENT_VERSION },
                        ua: { value: this.clientInfo.ua },

                        // Copy ServerState
                        connectionId: { value: serverState.connectionId },
                        connectedClientId: { value: serverState.connectedClientId },
                        domId: { value: serverState.domId },
                        url: { value: serverState.unsecureUrl },
                        surl: { value: serverState.url },
                        ttlInSecs: { value: serverState.getRemainingTtlInSec() },

                        // Copy this.connectedInfo
                        numberOfPingReplies: { value: this.connectedInfo.numberOfPingReplies },
                        connectedTimestamp: { value: this.connectedInfo.connectedTimestamp },
                        isNewUrl: { value: this.connectedInfo.isNewUrl },
                        transportType: { value: this.connectedInfo.transportType },
                        connectionNumber: { value: this.connectedInfo.connectionNumber }
                    }
                };

                this.copyProperties(clientEvent.properties, eventProperties);

                this.eventLogger.logEvent(clientEvent);
            }
        } catch (exc) {
            this.logger.warn(`error in sending event ${eventName}: ${toJson(exc)}`);
        }
    }

    public createStep(stepName: string, operation: string, error?: string): TrackerStep {
        return new TrackerStep(
            stepName,
            operation,
            this.beginTimestamp.duration,
            Date.now(),
            error);
    }

    public addStep(tag: string, progressHint: string, error?: string): void {
        if (this.eventLogSettings.enabled === false) {
            return;
        }

        if (this.steps.length === 0) {
            this.beginTimestamp.reset();
        }
        this.steps.push(this.createStep(tag, progressHint, error));
        ++this.totalStepCount;
        // do not maintain an ever growing list of steps. send a progress event with what is collected so far and clear steps
        // there should be enough context in any event for the server to group events created for the sameonnection
        if (this.eventLogSettings.numberOfStepsToMaintain !== undefined
            && this.steps.length > this.eventLogSettings.numberOfStepsToMaintain
        ) {
            const progress = this.steps.slice(0);
            this.steps.length = 0;
            this.sendTelemetry(ClientEventName.Progress, { reason: 'flush' }, progress);
        }
    }

    public trackStart(tag: string): void {
        this.addStep(tag, 'start');
    }

    public trackEnd(tag: string): void {
        this.addStep(tag, 'end');
    }

    public trackError(tag: string, error: string, addStep: boolean = true, subOperation?: string) {
        if (tag === 'health' && this.eventLogSettings.logHealthCheckError !== true) {
            return;
        }
        if (tag === 'ping' && this.eventLogSettings.logSendPingError === false) {
            return;
        }

        if (subOperation === undefined) {
            subOperation = 'error';
        }

        if (addStep === true) {
            this.addStep(tag, subOperation, error);
        }
        this.sendTelemetry(ClientEventName.Error, {}, [this.createStep(tag, subOperation, error)]);
    }

    public trackProgress(tag: string, subOperation: string): void {
        this.addStep(tag, subOperation);
    }

    public trackConnected(isNewUrl: boolean, transportType: string): void {
        this.setConnectedInfo(isNewUrl, transportType);
        const progress = this.steps.slice(0);
        const totalSteps = this.totalStepCount;
        const connectionDuration = this.beginTimestamp.duration;

        // clear steps collected
        this.steps.length = 0;
        this.totalStepCount = 0;

        this.sendTelemetry(ClientEventName.Connected, {
            stepCount: progress.length,
            totalStepCount: totalSteps,
            connectionEstablishmentMs_Total: connectionDuration
        }, progress);

        this.cancelProgressTimer();
    }

    public getSessionLength(): number {
        return Date.now() - this.connectedInfo.connectedTimestamp;
    }

    public trackDisconnected(context: Properties): void {
        context.sessionLengthMS = this.getSessionLength();
        this.sendTelemetry(ClientEventName.Disconnected, context, []);
        this.resetProgressSendTimer();
    }

    public trackNewConnection(): void {
        ++this.connectionAttempt;
    }

    public trackRequest(request?: ITrouterRequest, errorMsg?: string): void {
        const context: Properties = {};
        if (errorMsg !== undefined) {
            context.hasError = true;
            context.error = errorMsg;
        }
        try {
            if (request) {
                context.requestID = request.id;
                context.httpMethod = request.method;
                context.url = request.url;
                context.bodyLength = request.body.length;
                context.shortUrl = request.shortUrl;
                context.requestTimeStamp = request.startTS;
                context.correlationVector = request.correlationVector;
                const headers = request.headers;
                for (const header of Object.keys(headers)) {
                    if (headers.hasOwnProperty(header)) {
                        context[header] = headers[header];
                    }
                }
            }
        } catch (exc) {
            context.hasError = true;
            context.error = `${context.error} error creating request context ${toJson(exc)}`;
        }

        this.sendTelemetry(ClientEventName.Request, context, []);
    }

    public trackResponse(request?: ITrouterRequest, latencyMS?: number, responseData?: ResponseData, errorMsg?: string): void {
        const context: Properties = {};
        if (errorMsg !== undefined) {
            context.hasError = true;
            context.error = errorMsg;
        }

        try {
            context.responseTimestamp = responseData !== undefined ? responseData.sentTS : Date.now();
            if (request) {
                context.requestID = request.id;
                context.httpMethod = request.method;
                context.shortUrl = request.shortUrl;
                context.correlationVector = request.correlationVector;
                const headers = request.headers;
                for (const header of Object.keys(headers)) {
                    if (headers.hasOwnProperty(header)) {
                        context[header] = headers[header];
                    }
                }
            }

            if (responseData) {
                context.latencyMS = latencyMS;
                context.responseCode = responseData.status;
                context.responseLength = responseData.body.length;
            }
        } catch (exc) {
            context.hasError = true;
            context.error = `${context.error} error creating response context ${toJson(exc)}`;
        }

        this.sendTelemetry(ClientEventName.Response, context, []);
    }

    public sendResponseError(errorMsg: string, request?: ITrouterRequest, responseData?: ResponseData): void {
        this.trackResponse(request, undefined, responseData, errorMsg);
    }

    public close(): void {
        this.sendProgress({
            reason: 'closed'
        });
        this.steps.length = 0;
        this.cancelProgressTimer();
    }

    public mergeSettings(settings?: TrouterTelemetrySettings) {
        if (!settings) {
            return;
        }

        this.eventLogSettings.numberOfStepsToMaintain = Math.min(40,
            Math.max(10, (settings.numberOfStepsToMaintain !== undefined ? settings.numberOfStepsToMaintain : 0)));

        // min of 55 secs and max allowed is 1 hr
        const newProgressTimeout = Math.min(3600,
            Math.max(55, settings.sendProgressTimeoutSecs !== undefined ? settings.sendProgressTimeoutSecs : 0));

        this.eventLogSettings.logHealthCheckError = settings.logHealthCheckError;
        this.eventLogSettings.logSendPingError = settings.logSendPingError;

        // Copy enable/disable flags for all telemetry events
        for (const iterator of Object.keys(ClientEventName).map(k => ClientEventName[k])) {
            if (settings.hasOwnProperty(iterator) && settings[iterator] !== undefined) {
                this.eventLogSettings[iterator] = settings[iterator];
            }
        }

        if (this.eventLogSettings.sendProgressTimeoutSecs !== newProgressTimeout) {
            this.eventLogSettings.sendProgressTimeoutSecs = newProgressTimeout;
            this.resetProgressSendTimer();
        }
    }
}



// WEBPACK FOOTER //
// ./lib_temp/ConnectionTracker.ts